// CTV-468 — Self-service analytics facade.
//
// One module, two sinks. Components NEVER call posthog/gtag directly — they
// import from here so the two providers stay in sync by construction.
//
//   PostHog → product analytics: funnels, retention, session replay.
//             Receives every event.
//   GA4     → marketing/acquisition + Google-Ads attribution.
//             Receives pageviews + the conversion events in GA4_FORWARD.
//
// Each provider self-disables when its env key is absent, so local dev and
// previews stay silent (no real keys committed — see .env.example). US-only
// product, so there is no consent gate; identify uses the Supabase user id
// only (never email/PII).
import posthog from 'posthog-js';
import { isSelfServiceDevelopment } from './env';

// Canonical event names. The single source of truth — never inline a raw
// string at a call site, or GA4 and PostHog will drift.
export const EVENTS = {
  // Funnel
  STATION_VIEWED: 'station_viewed',
  WIZARD_STARTED: 'wizard_started',
  WIZARD_STEP_VIEWED: 'wizard_step_viewed',
  WIZARD_STEP_COMPLETED: 'wizard_step_completed',
  // Auth
  OTP_REQUESTED: 'otp_requested',
  OTP_VERIFIED: 'otp_verified',
  OTP_FAILED: 'otp_failed',
  // Conversion
  PAYMENT_STARTED: 'payment_started',
  SUBMISSION_COMPLETED: 'submission_completed',
  // Friction (codes/categories only — never raw messages or payloads)
  PAYMENT_FAILED: 'payment_failed',
  // CTV-637: distinct from PAYMENT_FAILED, which only covers a failure to
  // *create* the intent. This fires when the buyer entered a valid card, we
  // created the submission + PaymentIntent, and the Stripe confirm then
  // failed — leaving an orphaned pending_payment row. That path was
  // previously untracked, so the most expensive drop-off in the funnel was
  // invisible.
  PAYMENT_CONFIRM_FAILED: 'payment_confirm_failed',
  // CTV-637: buyer clicked "Finish checkout" on a campaign whose payment never
  // completed. Pairs with PAYMENT_CONFIRM_FAILED to measure how many stranded
  // checkouts we actually recover.
  CHECKOUT_RESUME_STARTED: 'checkout_resume_started',
  // CTV-637: buyer discarded a stranded checkout instead of finishing it. With
  // PAYMENT_CONFIRM_FAILED and CHECKOUT_RESUME_STARTED this is the full recovery
  // funnel — replaces the "abandoned" submission status the sweeper would have
  // set, so the metric lives here rather than in the schema.
  CHECKOUT_DISCARDED: 'checkout_discarded',
  CAMPAIGN_NAME_CONFLICT: 'campaign_name_conflict',
  CREATIVE_UPLOAD_FAILED: 'creative_upload_failed',
  // CTV-733: fires when the step-2 inventory verdict resolves infeasible (the
  // warning card the buyer sees), so gate-caused drop-off is attributable.
  FEASIBILITY_BLOCKED: 'feasibility_blocked',
  // CTV-733: buyer or publisher submitted an Ask-a-Developer support ticket.
  HELP_TICKET_SUBMITTED: 'help_ticket_submitted',
  // CTV-733: a referral/promo code failed validation on the review step
  // (code_type + reason; debounced per pause, so treat as attempts not people).
  CODE_VALIDATION_FAILED: 'code_validation_failed',
  // Creative — direct upload
  CREATIVE_UPLOAD_STARTED: 'creative_upload_started',
  CREATIVE_UPLOAD_SUCCEEDED: 'creative_upload_succeeded',
  // Creative — Waymark builder lifecycle
  WAYMARK_GENERATION_STARTED: 'waymark_generation_started',
  WAYMARK_GENERATION_FAILED: 'waymark_generation_failed',
  WAYMARK_TOPIC_FAILED: 'waymark_topic_failed',
  WAYMARK_READY: 'waymark_ready',
  WAYMARK_EDITOR_OPENED: 'waymark_editor_opened',
  WAYMARK_EDITOR_EXITED: 'waymark_editor_exited',
  WAYMARK_VIDEO_COMPLETED: 'waymark_video_completed',
  WAYMARK_VIDEO_RENDERED: 'waymark_video_rendered',
  WAYMARK_COMMITTED: 'waymark_committed',
  WAYMARK_REJECTED_SPECS: 'waymark_rejected_specs',
  // CTV-775: multi-commercial verbs + the restyled disclosure row. Every
  // Waymark event also carries `surface` ('wizard' | 'swap') and
  // `commercial_ordinal` (1-based position of the AI commercial on the
  // surface) via registerWaymarkContext below.
  WAYMARK_MAKE_ANOTHER_CLICKED: 'waymark_make_another_clicked',
  WAYMARK_START_OVER_CLICKED: 'waymark_start_over_clicked',
  WAYMARK_BANNER_EXPANDED: 'waymark_banner_expanded',
  WAYMARK_BANNER_PILL_CLICKED: 'waymark_banner_pill_clicked',
  WAYMARK_FAQ_OPENED: 'waymark_faq_opened',
  // Creative — Waymark editor bridge health (CTV-604 framing-refusal recovery)
  WAYMARK_BRIDGE_UNHEALTHY: 'waymark_bridge_unhealthy',
  WAYMARK_SDK_REINIT: 'waymark_sdk_reinit',
  // CTV-800: the closed-editor focus guard handed focus back from the hidden SDK iframe.
  WAYMARK_FOCUS_RECLAIMED: 'waymark_focus_reclaimed',
  // CTV-800: the read-to-generation handoff, by the subscription (event) or the resume probe.
  WAYMARK_TOPIC_HANDOFF: 'waymark_topic_handoff',
  WAYMARK_EDITOR_RECOVERED: 'waymark_editor_recovered',
  WAYMARK_EDITOR_RECOVERY_FAILED: 'waymark_editor_recovery_failed',
  WAYMARK_SDK_ERROR: 'waymark_sdk_error',
  // Drafts / resume
  DRAFT_CREATED: 'draft_created',
  DRAFT_RESUMED: 'draft_resumed',
  // Retention
  MY_CAMPAIGNS_VIEWED: 'my_campaigns_viewed',
  CAMPAIGN_DETAIL_VIEWED: 'campaign_detail_viewed',
  DELIVERY_REPORT_VIEWED: 'delivery_report_viewed',
  CAMPAIGN_RERUN_STARTED: 'campaign_rerun_started',
  // Campaign management — post-launch (CTV-440 Edit modal: extend / swap /
  // cancel, plus report/receipt downloads and failed-payment recovery)
  EDIT_CAMPAIGN_OPENED: 'edit_campaign_opened',
  CAMPAIGN_EXTEND_STARTED: 'campaign_extend_started',
  CAMPAIGN_EXTENDED: 'campaign_extended',
  CAMPAIGN_EXTEND_FAILED: 'campaign_extend_failed',
  CAMPAIGN_CANCELED: 'campaign_canceled',
  // CTV-466 replaced the single-file swap flow with a multi-creative
  // "edit commercial set" editor (EditCreativesTabPanel → /update-creative-set).
  // One event on a successful save; the old swap_started/swapped/voided are gone.
  CREATIVE_SET_UPDATED: 'creative_set_updated',
  DELIVERY_REPORT_DOWNLOADED: 'delivery_report_downloaded',
  RECEIPT_DOWNLOADED: 'receipt_downloaded',
  PAYMENT_RECOVERY_STARTED: 'payment_recovery_started',
  PAYMENT_RECOVERED: 'payment_recovered',
  // Account / team management (advertiser-side)
  TEAM_MEMBER_INVITED: 'team_member_invited',
  TEAM_MEMBER_REVOKED: 'team_member_revoked',
  ADVERTISER_PROFILE_UPDATED: 'advertiser_profile_updated',
  INVITATION_ACCEPTED: 'invitation_accepted',
  CAMPAIGN_ATTENTION_ACKED: 'campaign_attention_acked',
  // Publisher portal — submission review & ops (a different audience; every
  // event carries the `role` super-property set in useAuth)
  PAYMENT_CAPTURED: 'payment_captured',
  CREATIVE_APPROVED: 'creative_approved',
  CREATIVE_REJECTED: 'creative_rejected',
  // CTV-733: fires on finalize-rejections (CTV-658 removed the submission-level
  // reject button, so finalizing per-creative rejections is the moment a
  // rejection outcome lands). The old creative_swap_approved /
  // creative_swap_rejected events retired with the CTV-611 swap banner; swap
  // rulings now surface as creative_approved / creative_rejected.
  SUBMISSION_REJECTED: 'submission_rejected',
  PUBLICA_PUSH_RETRIED: 'publica_push_retried',
} as const;

// Events mirrored to GA4 (conversions). Everything reaches PostHog regardless;
// only these are forwarded to GA4 so marketing can attribute them to a source.
// CTV-733: campaign_extended forwards too so extension revenue counts toward
// Google Ads value optimization, not just the initial purchase.
const GA4_FORWARD = new Set<string>([
  EVENTS.OTP_VERIFIED,
  EVENTS.SUBMISSION_COMPLETED,
  EVENTS.CAMPAIGN_EXTENDED,
]);

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let posthogReady = false;
let ga4Ready = false;
// Guard so AuthProvider re-mounts (the router wraps each route in its own
// provider) don't re-fire identify on every navigation.
let lastIdentified: string | null = null;

function initGa4(measurementId: string): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // gtag.js consumes the native `arguments` object pushed verbatim. Pushing a
  // plain array instead makes GTM treat the entry as data — not a js/config/
  // event command — so it silently never sends a single beacon. Keep Google's
  // canonical classic-function shape; do NOT refactor this to push an array.
  const gtag: (...args: unknown[]) => void = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag = gtag;
  gtag('js', new Date());
  // Manual pageviews — this is a SPA, so gtag's auto page_view would only fire
  // on the initial hard load. page() handles client-side navigation.
  gtag('config', measurementId, { send_page_view: false });
  ga4Ready = true;
}

// Initialize both providers. Reads env lazily (not at module load) so tests
// can stub import.meta.env per-case. Safe to call once at app boot.
export function init(): void {
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  // `||` not `??`: an env var that exists but is blank (Render renders unset
  // vars as "") must still fall back, otherwise posthog-js posts to our own
  // origin and every event 404s. Bit prod on 2026-08-16.
  const posthogHost =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined)?.trim() || 'https://us.i.posthog.com';
  const ga4Id = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;

  if (posthogKey && !posthogReady) {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      // Explicit events only — no autocapture noise; the funnel is hand-built.
      autocapture: false,
      capture_pageview: false,
      // Capture $pageleave (tab-close / navigate-away) so funnel drop-off and
      // time-on-step are attributable. Default is 'if_capture_pageview', which
      // would disable it since we send manual pageviews — so set it explicitly.
      capture_pageleave: true,
      // Don't mint a person profile for every anonymous visitor; funnel events
      // are still captured anonymously and stitched on identify().
      person_profiles: 'identified_only',
      // Session replay on, but never record what users type. Stripe card fields
      // live in a cross-origin iframe and are never in our DOM regardless.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
      },
    });
    posthogReady = true;
  }

  if (ga4Id && !ga4Ready) {
    initGa4(ga4Id);
  }

  // Tag every event with the environment. Separate PostHog projects (keyed by
  // a per-environment VITE_POSTHOG_KEY) are the real dev/prod boundary; this is
  // belt-and-suspenders so dev/prod is filterable within a project and obvious
  // on raw events.
  if (posthogReady || ga4Ready) {
    registerContext({ environment: isSelfServiceDevelopment() ? 'dev' : 'prod' });
  }
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

// Publisher redirect targets carry utm_* params so arrivals are attributable
// (the redirect handoff doc / CTV-784 templates). The visitor shouldn't have
// to stare at them, and a copied/bookmarked URL shouldn't re-attribute a later
// visit — so once captured, they're scrubbed from the address bar.
//
// ORDER MATTERS: call this AFTER init() and AFTER the first page(). GA4 reads
// campaign params from page_location at the first event of the session, and
// posthog-js reads them from the live URL per event — both must see the full
// URL once. From then on the PostHog values ride as SESSION-scoped
// super-properties (register_for_session, not register: a persistent super-
// property would stamp next week's direct visit with this week's campaign),
// so station_viewed / funnel events keep their utm_source after the strip.
// Ad-platform click ids (gclid, fbclid) are left in place — GA owns their
// lifecycle and they never appear on our own redirect links.
export function captureAndStripCampaignParams(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const found: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = url.searchParams.get(key);
    if (value) {
      found[key] = value;
      url.searchParams.delete(key);
    }
  }
  if (Object.keys(found).length === 0) return;
  if (posthogReady) posthog.register_for_session(found);
  // Strip regardless of provider state: the cosmetic cleanup shouldn't depend
  // on analytics keys being configured. replaceState keeps the ugly URL out of
  // the back stack, mirroring codeCapture's ?ref=/?promo= handling.
  window.history.replaceState(
    {},
    '',
    url.pathname + (url.search ? '?' + url.searchParams.toString() : '') + url.hash,
  );
}

// Cross-cutting context attached to EVERY subsequent event (PostHog
// super-properties + GA4 default params). Call when station/auth context
// becomes known or changes — e.g. { station_slug } and { auth_state }.
export function registerContext(props: Record<string, unknown>): void {
  if (posthogReady) posthog.register(props);
  if (ga4Ready && window.gtag) window.gtag('set', props);
}

export function page(path: string): void {
  if (posthogReady) posthog.capture('$pageview', { path });
  if (ga4Ready && window.gtag) window.gtag('event', 'page_view', { page_path: path });
}

// CTV-775: cross-cutting Waymark context. The provider / surface registers
// which surface (wizard | swap) and which AI commercial (1-based ordinal) is
// active; every `waymark_*` event then carries both without each call site
// threading them through. Module state, not a super-property: it must not
// leak onto non-Waymark events, and it changes as the active commercial does.
// CTV-800: 'studio' is the staff surface at /admin/studio.
export type WaymarkSurface = 'wizard' | 'swap' | 'studio';
let waymarkContext: { surface: WaymarkSurface; commercial_ordinal: number } | null = null;

export function registerWaymarkContext(
  ctx: { surface: WaymarkSurface; commercialOrdinal: number } | null,
): void {
  waymarkContext = ctx ? { surface: ctx.surface, commercial_ordinal: ctx.commercialOrdinal } : null;
}

/** Test seam: what the next waymark_* event will carry. */
export function currentWaymarkContext(): { surface: WaymarkSurface; commercial_ordinal: number } | null {
  return waymarkContext;
}

export function track(event: string, props?: Record<string, unknown>): void {
  const enriched =
    waymarkContext && event.startsWith('waymark_') ? { ...waymarkContext, ...props } : props;
  if (posthogReady) posthog.capture(event, enriched);
  if (ga4Ready && window.gtag && GA4_FORWARD.has(event)) {
    window.gtag('event', event, enriched ?? {});
  }
}

// User-id only — no email/PII. Idempotent across provider re-mounts.
export function identify(userId: string): void {
  if (!userId || userId === lastIdentified) return;
  lastIdentified = userId;
  if (posthogReady) posthog.identify(userId);
  if (ga4Ready && window.gtag) window.gtag('set', { user_id: userId });
}

export function reset(): void {
  if (lastIdentified === null) return;
  lastIdentified = null;
  if (posthogReady) posthog.reset();
  if (ga4Ready && window.gtag) window.gtag('set', { user_id: undefined });
}
